import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiUser,
  FiCreditCard,
  FiMail,
  FiPhone,
  FiMapPin,
  FiCalendar,
  FiAward,
  FiShield,
  FiCheck,
  FiCopy,
  FiShare2,
  FiDownload,
  FiEdit2,
  FiStar,
  FiUsers,
  FiGlobe,
  FiHome,
  FiLock
} from 'react-icons/fi';
import {
  FaUserCircle,
  FaIdCard,
  FaGooglePay,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCity,
  FaFlag,
  FaCode,
  FaUserFriends,
  FaHandshake,
  FaGem,
  FaCrown,
  FaUser,
  FaBuilding,
  FaGlobe
} from 'react-icons/fa';
import { toast } from 'react-toastify';

const ShowProfile = () => {
  const [activeTab, setActiveTab] = useState('personal');
  const [copiedField, setCopiedField] = useState(null);
  const userData = useSelector(state => state.user_info[0]?.user);

  const userDetails = {
    name: "ARCHANA DEVI",
    id: "RVI865271",
    mobile: "8808613763",
    sponsorId: "RVI258471",
    guardianName: "",
    nomineeName: "",
    email: userData?.email,
    address: userData?.address,
    city: userData?.city,
    state: userData?.state,
    country: userData?.country,
    pinCode: userData?.pincode,
    nomineeName: userData?.nominee_name,
    guardianName: userData?.father_name,
    bankDetails: {
      bankName: userData?.bank_name,
      accountHolderName: userData?.account_holder_name,
      branchName: userData?.branch_name,
      accountNo: userData?.bank_account_number,
      ifscCode: userData?.ifsc_code,
      panNo: userData?.pan_number
    },
    walletDetails: {
      phonepeId: userData?.phonepeId,
      googlePayId: userData?.googlePayId
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success(`${field} copied to clipboard!`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const PersonalProfileTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="p-4 sm:p-6 lg:p-8"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        {/* Profile Card - Left Column */}
        <div className="lg:col-span-4">
          <div className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 rounded-2xl shadow-xl overflow-hidden border border-indigo-100">
            {/* Cover Image */}
            <div className="h-24 bg-gradient-to-r from-indigo-500 to-purple-600 relative">
              <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2">
                <div className="relative">
                  <div className="w-28 h-28 sm:w-32 sm:h-32 bg-white rounded-full p-1 shadow-xl">
                    {userData?.profile_image ? (
                      <img 
                        src={userData.profile_image} 
                        alt="Profile" 
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                        <FaUserCircle className="w-20 h-20 text-white" />
                      </div>
                    )}
                  </div>
                  <button className="absolute bottom-2 right-2 bg-white rounded-full p-1.5 shadow-md hover:scale-110 transition">
                    <FiEdit2 className="w-3 h-3 text-indigo-600" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Profile Info */}
            <div className="pt-16 pb-6 px-6 text-center">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1">{userData?.fullName}</h2>
              <p className="text-indigo-600 font-mono text-sm mb-3">{userData?.sponsorCode}</p>
              
              {/* Status Badge */}
              <div className="inline-flex items-center gap-2 bg-emerald-100 px-3 py-1 rounded-full mb-4">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-semibold text-emerald-700">Active Member</span>
              </div>
              
              {/* Contact Info */}
              <div className="space-y-2 text-left mt-4">
                <div className="flex items-center gap-3 text-gray-600 p-2 hover:bg-indigo-50 rounded-lg transition cursor-pointer group"
                     onClick={() => copyToClipboard(userData?.phone, 'Phone Number')}>
                  <FiPhone className="w-4 h-4 text-indigo-500" />
                  <span className="text-sm flex-1">{userData?.phone}</span>
                  {copiedField === 'Phone Number' ? (
                    <FiCheck className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <FiCopy className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition" />
                  )}
                </div>
                <div className="flex items-center gap-3 text-gray-600 p-2 hover:bg-indigo-50 rounded-lg transition cursor-pointer group"
                     onClick={() => copyToClipboard(userData?.email, 'Email')}>
                  <FiMail className="w-4 h-4 text-indigo-500" />
                  <span className="text-sm flex-1 truncate">{userData?.email}</span>
                  {copiedField === 'Email' ? (
                    <FiCheck className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <FiCopy className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition" />
                  )}
                </div>
              </div>
            </div>
            
            {/* Stats */}
            <div className="border-t border-indigo-100 px-6 py-4 bg-indigo-50/50">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-indigo-600">5</p>
                  <p className="text-xs text-gray-500">Direct Referrals</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-indigo-600">12</p>
                  <p className="text-xs text-gray-500">Team Members</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Details Grid - Right Column */}
        <div className="lg:col-span-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
              <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                <FiUser className="w-5 h-5" />
                Personal Information
              </h3>
              <p className="text-indigo-100 text-sm mt-1">Complete profile details</p>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { label: 'Sponsor ID', value: userData?.sponsor, icon: FaCode, color: 'indigo' },
                  { label: 'Email Address', value: userData?.email, icon: FaEnvelope, color: 'blue' },
                  { label: 'Phone Number', value: userData?.phone, icon: FaPhoneAlt, color: 'green' },
                  { label: 'Address', value: userData?.address, icon: FaMapMarkerAlt, color: 'orange' },
                  { label: 'City', value: userData?.city, icon: FaCity, color: 'purple' },
                  { label: 'State', value: userData?.state, icon: FaBuilding, color: 'teal' },
                  { label: 'Country', value: userData?.country, icon: FaGlobe, color: 'cyan' },
                  { label: 'Pin Code', value: userData?.pincode, icon: FiMapPin, color: 'pink' },
                  { label: 'Nominee Name', value: userData?.nominee_name, icon: FaUserFriends, color: 'amber' },
                  { label: 'Guardian Name', value: userData?.father_name, icon: FaHandshake, color: 'lime' }
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`bg-gradient-to-r from-${item.color}-50 to-white p-4 rounded-xl border border-${item.color}-100 hover:shadow-md transition-all cursor-pointer group`}
                    onClick={() => item.value && copyToClipboard(item.value, item.label)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <item.icon className={`w-4 h-4 text-${item.color}-500`} />
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{item.label}</p>
                        </div>
                        <p className="text-gray-800 font-medium text-sm">{item.value || 'Not provided'}</p>
                      </div>
                      {item.value && (
                        <div className="opacity-0 group-hover:opacity-100 transition">
                          {copiedField === item.label ? (
                            <FiCheck className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <FiCopy className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const BankProfileTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="p-4 sm:p-6 lg:p-8"
    >
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <FaEnvelope className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Bank Account Details</h3>
                <p className="text-emerald-100 text-sm">Securely stored banking information</p>
              </div>
            </div>
          </div>

          {/* Bank Details Grid */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[
                { label: 'Bank Name', value: userDetails.bankDetails.bankName, icon: FaEnvelope, color: 'emerald' },
                { label: 'Account Holder Name', value: userDetails.bankDetails.accountHolderName, icon: FaUser, color: 'blue' },
                { label: 'Branch Name', value: userDetails.bankDetails.branchName, icon: FaBuilding, color: 'purple' },
                { label: 'Account Number', value: userDetails.bankDetails.accountNo, icon: FaIdCard, color: 'indigo' },
                { label: 'IFSC Code', value: userDetails.bankDetails.ifscCode, icon: FaCode, color: 'orange' },
                { label: 'PAN Number', value: userDetails.bankDetails.panNo, icon: FiShield, color: 'red' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                  className={`bg-gradient-to-r from-${item.color}-50 to-white p-5 rounded-xl border border-${item.color}-100 hover:shadow-lg transition-all cursor-pointer group`}
                  onClick={() => item.value && copyToClipboard(item.value, item.label)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <item.icon className={`w-4 h-4 text-${item.color}-500`} />
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{item.label}</p>
                      </div>
                      <p className="text-gray-800 font-medium text-sm break-all">
                        {item.value || 'Not provided'}
                      </p>
                    </div>
                    {item.value && (
                      <div className="opacity-0 group-hover:opacity-100 transition ml-2">
                        {copiedField === item.label ? (
                          <FiCheck className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <FiCopy className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Security Notice */}
            <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex items-start gap-3">
                <FiLock className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Secure Information</p>
                  <p className="text-xs text-amber-700 mt-1">Your bank details are encrypted and securely stored. Never share your sensitive information with anyone.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const WalletDetailsTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="p-4 sm:p-6 lg:p-8"
    >
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <FaEnvelope className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Wallet & Payment Details</h3>
                <p className="text-purple-100 text-sm">UPI IDs for quick payments</p>
              </div>
            </div>
          </div>

          {/* Wallet Details */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* PhonePe Card */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-xl border border-indigo-200 hover:shadow-xl transition-all cursor-pointer group"
                onClick={() => userDetails.walletDetails.phonepeId && copyToClipboard(userDetails.walletDetails.phonepeId, 'PhonePe ID')}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
                      <FaGooglePay className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-indigo-600 uppercase">UPI ID</p>
                      <p className="font-bold text-gray-800 text-lg">PhonePe</p>
                    </div>
                  </div>
                  {userDetails.walletDetails.phonepeId && (
                    <div className="opacity-0 group-hover:opacity-100 transition">
                      {copiedField === 'PhonePe ID' ? (
                        <FiCheck className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <FiCopy className="w-5 h-5 text-indigo-400" />
                      )}
                    </div>
                  )}
                </div>
                <p className="text-gray-700 font-mono text-lg tracking-wider">
                  {userDetails.walletDetails.phonepeId || 'Not provided'}
                </p>
                {userDetails.walletDetails.phonepeId && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-indigo-600">
                    <FiCheck className="w-3 h-3" />
                    <span>Verified UPI ID</span>
                  </div>
                )}
              </motion.div>

              {/* Google Pay Card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-xl border border-emerald-200 hover:shadow-xl transition-all cursor-pointer group"
                onClick={() => userDetails.walletDetails.googlePayId && copyToClipboard(userDetails.walletDetails.googlePayId, 'Google Pay ID')}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                      <FaGooglePay className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-emerald-600 uppercase">UPI ID</p>
                      <p className="font-bold text-gray-800 text-lg">Google Pay</p>
                    </div>
                  </div>
                  {userDetails.walletDetails.googlePayId && (
                    <div className="opacity-0 group-hover:opacity-100 transition">
                      {copiedField === 'Google Pay ID' ? (
                        <FiCheck className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <FiCopy className="w-5 h-5 text-emerald-400" />
                      )}
                    </div>
                  )}
                </div>
                <p className="text-gray-700 font-mono text-lg tracking-wider">
                  {userDetails.walletDetails.googlePayId || 'Not provided'}
                </p>
                {userDetails.walletDetails.googlePayId && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-emerald-600">
                    <FiCheck className="w-3 h-3" />
                    <span>Verified UPI ID</span>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Payment Info */}
            <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-start gap-3">
                <FiShield className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">Secure Payments</p>
                  <p className="text-xs text-blue-700 mt-1">All payments are processed through secure UPI channels. Your payment information is encrypted and protected.</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-6 grid grid-cols-2 gap-4">
              <button className="flex items-center justify-center gap-2 py-3 bg-indigo-50 text-indigo-700 rounded-xl font-medium hover:bg-indigo-100 transition">
                <FiDownload className="w-4 h-4" />
                Transaction History
              </button>
              <button className="flex items-center justify-center gap-2 py-3 bg-emerald-50 text-emerald-700 rounded-xl font-medium hover:bg-emerald-100 transition">
                <FiShare2 className="w-4 h-4" />
                Request Payment
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-4 sm:py-6 lg:py-8">
      <div className="container mx-auto px-3 sm:px-4 lg:px-6">
        {/* Page Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                My Profile
              </h1>
              <p className="text-gray-500 mt-1 text-sm">Manage your personal and financial information</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-semibold text-emerald-700">Verified Account</span>
              </span>
              <button className="px-4 py-1.5 bg-white rounded-lg shadow-sm text-gray-600 hover:shadow-md transition text-sm">
                <FiEdit2 className="inline mr-1 w-3 h-3" /> Edit
              </button>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          {/* Tab Navigation */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600">
            <div className="flex flex-wrap">
              {[
                { id: 'personal', label: 'Personal Profile', icon: FiUser },
                { id: 'bank', label: 'Bank Details', icon: FaEnvelope },
                { id: 'wallet', label: 'Wallet Details', icon: FaEnvelope }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 font-medium transition-all duration-200 relative ${
                    activeTab === tab.id
                      ? 'text-white'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  <tab.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-sm sm:text-base">{tab.label}</span>
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-400 to-teal-400"
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {activeTab === 'personal' && <PersonalProfileTab />}
            {activeTab === 'bank' && <BankProfileTab />}
            {activeTab === 'wallet' && <WalletDetailsTab />}
          </AnimatePresence>
        </div>

        {/* Footer Note */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400">
            Last updated: {new Date().toLocaleDateString()} • Information is encrypted and secure
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShowProfile;